import { useEffect, useMemo, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Package } from "lucide-react";
import {
  useCreateStandardCarousel,
  useUpdateStandardCarousel,
  useUpdateStandardCarouselProducts,
} from "@admin/hooks/useAdminCarousels";
import { useProducts, useDebounce } from "@admin/hooks";
import type { StandardCarouselRecord } from "@shared/types/carousel";
import type { Product } from "@shared/types/product";
import { nameToSlug } from "@shared/utils/slugUtils";
import { ModalWithTabs, type Tab } from "@admin/components/ui/ModalWithTabs";

const optionalString = (max: number, message: string) =>
  z
    .string()
    .max(max, message)
    .optional()
    .transform((value) => {
      if (value === undefined) return undefined;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    });

const layoutOptions = [
  { value: "grid", label: "Grid" },
  { value: "carousel", label: "Carousel" },
  { value: "highlight", label: "Destacados" },
] as const;

const layoutEnum = z.enum(["grid", "carousel", "highlight"]);

const standardCarouselSchema = z.object({
  name: z
    .string()
    .min(3, "El nombre debe tener al menos 3 caracteres")
    .max(120, "El nombre no puede superar los 120 caracteres")
    .transform((value) => value.trim()),
  description: optionalString(
    320,
    "La descripción no puede superar los 320 caracteres"
  ),
  layout: layoutEnum,
  order: z
    .coerce.number()
    .int("El orden debe ser un número entero")
    .min(0, "El orden debe ser mayor o igual a 0")
    .max(9999, "El orden no puede superar 9999"),
  isActive: z.boolean(),
  productIds: z
    .array(z.string().uuid("ID de producto inválido"))
    .min(1, "Debes seleccionar al menos un producto"),
});

type StandardCarouselFormInput = z.input<typeof standardCarouselSchema>;
type StandardCarouselFormOutput = z.output<typeof standardCarouselSchema>;

interface StandardCarouselFormProps {
  initialData?: StandardCarouselRecord;
  existingCarousels: StandardCarouselRecord[];
  onClose: () => void;
  onSuccess: (message: { title: string; description: string }) => void;
  onError: (message: { title: string; description: string }) => void;
}

export function StandardCarouselForm({
  initialData,
  existingCarousels,
  onClose,
  onSuccess,
  onError,
}: StandardCarouselFormProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    setError,
    watch,
  } = useForm<StandardCarouselFormInput>({
    resolver: zodResolver(standardCarouselSchema),
    defaultValues: {
      name: initialData?.name ?? "",
      description: initialData?.description ?? "",
      layout:
        (initialData?.layout as (typeof layoutOptions)[number]["value"] | undefined) ??
        "grid",
      order: initialData?.order ?? 0,
      isActive: initialData?.isActive ?? true,
      productIds: initialData?.products.map((product) => product.id) ?? [],
    },
    mode: "onChange",
  });

  const selectedProductIds = watch("productIds");

  const { data: productsResponse, isLoading: isLoadingProducts } = useProducts({
    page: currentPage,
    limit: 10,
    search: debouncedSearch || undefined,
  });

  const products = productsResponse?.data ?? [];
  const paginationMeta = productsResponse?.pagination;

  const selectedProducts = useMemo(() => {
    const map = new Map<string, Product>();
    if (initialData?.products) {
      initialData.products.forEach((product) => {
        map.set(product.id, {
          id: product.id,
          name: product.name,
          slug: product.slug,
        } as Product);
      });
    }

    products.forEach((product) => {
      map.set(product.id, product);
    });

    return selectedProductIds
      .map((productId) => map.get(productId))
      .filter((product): product is Product => Boolean(product));
  }, [initialData?.products, selectedProductIds, products]);

  const { mutateAsync: createStandardCarousel, isPending: isCreating } =
    useCreateStandardCarousel();
  const { mutateAsync: updateStandardCarousel, isPending: isUpdating } =
    useUpdateStandardCarousel();
  const { mutateAsync: updateStandardCarouselProducts, isPending: isUpdatingProducts } =
    useUpdateStandardCarouselProducts();

  useEffect(() => {
    if (paginationMeta) {
      const totalPages = paginationMeta.totalPages;
      if (currentPage > totalPages) {
        setCurrentPage(totalPages || 1);
      }
    }
  }, [paginationMeta, currentPage]);

  const existingNames = useMemo(
    () =>
      existingCarousels
        .filter((carousel) => carousel.id !== initialData?.id)
        .map((carousel) => carousel.name.toLowerCase()),
    [existingCarousels, initialData?.id]
  );

  const handleSelectProduct = (productId: string) => {
    setValue(
      "productIds",
      Array.from(new Set([...selectedProductIds, productId])),
      { shouldValidate: true }
    );
  };

  const handleRemoveProduct = (productId: string) => {
    setValue(
      "productIds",
      selectedProductIds.filter((id) => id !== productId),
      { shouldValidate: true }
    );
  };

  const onSubmit: SubmitHandler<StandardCarouselFormInput> = async (formValues) => {
    const parsed: StandardCarouselFormOutput = standardCarouselSchema.parse(formValues);

    if (existingNames.includes(parsed.name.toLowerCase())) {
      setError("name", {
        type: "manual",
        message: "Ya existe un carrusel standard con este nombre",
      });
      return;
    }

    try {
      const payload = {
        name: parsed.name.trim(),
        description: parsed.description,
        layout: parsed.layout,
        order: parsed.order,
        isActive: parsed.isActive,
        productIds: parsed.productIds,
      };

      if (initialData) {
        await updateStandardCarousel({
          id: initialData.id,
          data: payload,
        });
        await updateStandardCarouselProducts({
          id: initialData.id,
          productIds: payload.productIds,
        });
        onSuccess({
          title: "Carrusel standard actualizado",
          description: `Se actualizó "${parsed.name}" correctamente.`,
        });
      } else {
        await createStandardCarousel(payload);
        onSuccess({
          title: "Carrusel standard creado",
          description: `Se creó "${parsed.name}" correctamente.`,
        });
      }

      onClose();
    } catch (error) {
      onError({
        title: "Error al guardar",
        description:
          "No se pudo guardar el carrusel standard. Revisa los datos e intenta nuevamente.",
      });
    }
  };

  // Definir los tabs
  const tabs: Tab[] = [
    {
      id: "general",
      label: "General",
      icon: <FileText className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                {...register("name")}
                type="text"
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.name
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Ej: Destacados baño"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                {...register("description")}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.description
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Detalle adicional para el carrusel"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Layout <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("layout")}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                    errors.layout
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Selecciona un layout</option>
                  {layoutOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.layout && (
                  <p className="mt-1 text-sm text-red-600">{errors.layout.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orden <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("order")}
                  type="number"
                  min={0}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                    errors.order
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="0"
                />
                {errors.order && (
                  <p className="mt-1 text-sm text-red-600">{errors.order.message}</p>
                )}
              </div>
            </div>

            <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                {...register("isActive")}
                className="h-4 w-4 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
              />
              <span>Marcar como activo</span>
            </label>
          </div>
        </div>
      ),
    },
    {
      id: "products",
      label: "Productos",
      icon: <Package className="w-4 h-4" />,
      content: (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Productos vinculados <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                Min: 1 producto seleccionado
              </span>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Buscar productos por nombre..."
                  className="w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] border-gray-300"
                />
                {searchTerm.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#282828]"
                  >
                    Limpiar
                  </button>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-100">
                {isLoadingProducts ? (
                  <div className="p-4 text-sm text-gray-500">Cargando productos...</div>
                ) : products.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">
                    No hay productos que coincidan con la búsqueda.
                  </div>
                ) : (
                  products.map((product) => {
                    const isSelected = selectedProductIds.includes(product.id);
                    return (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() =>
                          isSelected
                            ? handleRemoveProduct(product.id)
                            : handleSelectProduct(product.id)
                        }
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          isSelected ? "bg-[#282828]/10" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {nameToSlug(product.name)}
                            </p>
                          </div>
                          <div
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              isSelected
                                ? "bg-[#282828] text-white"
                                : "bg-gray-200 text-gray-700"
                            }`}
                          >
                            {isSelected ? "Seleccionado" : "Agregar"}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {paginationMeta && paginationMeta.totalPages > 1 && (
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={!paginationMeta.hasPrev}
                    className={`px-2 py-1 border rounded-md ${
                      paginationMeta.hasPrev
                        ? "border-gray-300 hover:bg-gray-50"
                        : "border-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {paginationMeta.page} de {paginationMeta.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(paginationMeta.totalPages, prev + 1)
                      )
                    }
                    disabled={!paginationMeta.hasNext}
                    className={`px-2 py-1 border rounded-md ${
                      paginationMeta.hasNext
                        ? "border-gray-300 hover:bg-gray-50"
                        : "border-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Productos seleccionados ({selectedProductIds.length})
              </p>
              {selectedProducts.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Agrega productos para construir el carrusel.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedProducts.map((product) => (
                    <li
                      key={product.id}
                      className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500">{product.slug}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(product.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Quitar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
        </div>
      ),
    },
  ];

  // Determinar qué tabs tienen errores
  const tabErrors = useMemo(() => {
    const generalFields = ["name", "description", "layout", "order", "isActive"];
    const productsFields = ["productIds"];

    const hasGeneralErrors = generalFields.some((field) => errors[field as keyof typeof errors]);
    const hasProductsErrors = productsFields.some((field) => errors[field as keyof typeof errors]);

    return {
      general: hasGeneralErrors,
      products: hasProductsErrors,
    };
  }, [errors]);

  // Footer con botones
  const footer = (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
        disabled={isSubmitting || isCreating || isUpdating || isUpdatingProducts}
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="standard-carousel-form"
        disabled={isSubmitting || isCreating || isUpdating || isUpdatingProducts}
        className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {isSubmitting || isCreating || isUpdating || isUpdatingProducts
          ? "Guardando..."
          : initialData
            ? "Guardar cambios"
            : "Crear carrusel"}
      </button>
    </div>
  );

  return (
    <ModalWithTabs
      isOpen={true}
      onClose={onClose}
      title={initialData ? "Editar Carrusel Standard" : "Crear Carrusel Standard"}
      tabs={tabs}
      size="xl"
      footer={footer}
      defaultTab="general"
      formId="standard-carousel-form"
      onSubmit={handleSubmit(onSubmit)}
      tabErrors={tabErrors}
    />
  );
}

